import { Injectable, inject } from "@angular/core";
import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Observable, catchError, throwError } from "rxjs";

import {
	parsePlanAccessErrorFromHttpError,
} from "../../modules/subscription/billing-operations.models";
import { PlanLimitModalService } from "../../modules/subscription/services/plan-limit-modal.service";
import { AuthService } from "../services/auth/auth.service";

@Injectable({ providedIn: "root" })
export class HttpErrorInterceptor implements HttpInterceptor {
	private readonly planLimitModal = inject(PlanLimitModalService);
	private readonly auth = inject(AuthService);
	private lastToastShownAt = 0;
	private readonly toastCooldownMs = 5000;

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(req).pipe(
			catchError((error: unknown) => {
				if (error instanceof HttpErrorResponse) {
					const planAccessError = parsePlanAccessErrorFromHttpError(error);
					if (planAccessError) {
						if (
							planAccessError.code !== "FEATURE_NOT_INCLUDED" &&
							this.shouldShowPlanLimitModal(req)
						) {
							this.planLimitModal.openFromPlanAccessError(planAccessError);
						}
						return throwError(() => error);
					}

					if (!this.shouldSuppressToast(req, error)) {
						const description = this.getErrorDescription(error);
						this.maybeShowToast(description, error.status);
					}
				}
				return throwError(() => error);
			})
		);
	}

	/** Invite/signup guests see inline errors on the auth page — not the upgrade modal. */
	private shouldShowPlanLimitModal(request: HttpRequest<unknown>): boolean {
		if (this.auth.isGuestAuthRoute()) {
			return false;
		}
		const url = request.url || "";
		if (
			url.includes("/auth/signup-in-workspace") ||
			url.includes("/auth/signup") ||
			url.includes("/auth/social/")
		) {
			return false;
		}
		return true;
	}

	private shouldSuppressToast(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
		if (request.responseType === "blob" || request.responseType === "arraybuffer") return true;
		const url = request.url || "";
		if (url.includes("/validate-reset-token")) return true;
		if (url.includes("/sign-in")) return true;
		if (url.includes("/auth/refresh")) return true;
		if (url.includes("/admin/sign-in")) return true;
		if (parsePlanAccessErrorFromHttpError(error)) return true;
		if (error.status === 404) return true;
		return false;
	}

	private getErrorDescription(error: HttpErrorResponse): string {
		// Client-side/network error
		const clientError = (error.error as any) as ErrorEvent | undefined;
		if (clientError && typeof (clientError as any).message === "string") {
			return clientError.message || "A network error occurred.";
		}
		// Server-side
		const body = (error as any)?.error;
		const apiMessage =
			(typeof body === "string" && body) ||
			(body && (body.message || body.error || body.code));
		if (apiMessage) return String(apiMessage);
		if (error.statusText) return `${error.status} ${error.statusText}`;
		return "Unexpected error. Please try again.";
	}

	private maybeShowToast(message: string, status: number): void {
		const now = Date.now();
		if (now - this.lastToastShownAt < this.toastCooldownMs) return;
		this.lastToastShownAt = now;
		// Minimal toast: replace with your toast service if available
		// eslint-disable-next-line no-console
		console.error(`Error (${status}): ${message}`);
	}
}
